<!-- Cancel Appointment Modal -->
@if (isOpen) {
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    (click)="closeModal()"
    (keydown.escape)="closeModal()"
  >
    <div
      class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6"
      (click)="$event.stopPropagation()"
      (keydown)="$event.stopPropagation()"
    >
      <div class="text-center mb-6">
        <h2 class="text-xl font-semibold text-primary mb-2">
          {{ translationService.translate('cancel.modal.title') }}
        </h2>
        <p class="text-sm text-gray-600">
          {{ translationService.translate('cancel.modal.subtitle') }}
        </p>
      </div>

      <!-- Current Appointment Info -->
      <div class="bg-gray-50 p-4 rounded-lg mb-6">
        <h3 class="text-lg font-semibold text-primary mb-2">
          {{ translationService.translate('cancel.current.title') }}
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-gray-600">{{ translationService.translate('cancel.current.date') }}</span>
            <span class="ml-2 font-medium">{{ formattedAppointmentDate() }}</span>
          </div>
          <div>
            <span class="text-gray-600">{{ translationService.translate('cancel.current.time') }}</span>
            <span class="ml-2 font-medium">{{ formattedAppointmentTime() }}</span>
          </div>
          <div class="sm:col-span-2">
            <span class="text-gray-600">{{ translationService.translate('cancel.current.patient') }}</span>
            <span class="ml-2 font-medium">{{ getPatientName() }}</span>
          </div>
        </div>
      </div>

      <!-- Cancel Form -->
      <div class="space-y-4 mb-6">
        <!-- Cancellation Reason -->
        <div>
          <label for="cancellation-reason" class="block text-sm font-medium text-gray-700 mb-2">
            {{ translationService.translate('cancel.reason.label') }} <span class="text-red-500">*</span>
          </label>
          <select
            id="cancellation-reason"
            [value]="cancelData().reason"
            (change)="onReasonChange($event, 'reason')"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          >
            <option value="">{{ translationService.translate('cancel.reason.placeholder') }}</option>
            <option value="doctor-emergency">
              {{ translationService.translate('cancel.reason.doctorEmergency') }}
            </option>
            <option value="doctor-illness">{{ translationService.translate('cancel.reason.doctorIllness') }}</option>
            <option value="schedule-conflict">
              {{ translationService.translate('cancel.reason.scheduleConflict') }}
            </option>
            <option value="medical-equipment">
              {{ translationService.translate('cancel.reason.medicalEquipment') }}
            </option>
            <option value="facility-maintenance">
              {{ translationService.translate('cancel.reason.facilityMaintenance') }}
            </option>
            <option value="other">{{ translationService.translate('cancel.reason.other') }}</option>
          </select>
        </div>

        <!-- Custom Reason Input (only shown when "other" is selected) -->
        @if (isOtherReasonSelected()) {
          <div>
            <label for="custom-reason" class="block text-sm font-medium text-gray-700 mb-2">
              {{ translationService.translate('cancel.customReason.label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="custom-reason"
              type="text"
              [value]="cancelData().customReason"
              (input)="onInputChange($event, 'customReason')"
              [placeholder]="translationService.translate('cancel.customReason.placeholder')"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
        }

        <!-- Proposed Alternative -->
        <div>
          <h3 class="text-lg font-semibold text-primary mb-3">
            {{ translationService.translate('cancel.alternative.title') }} <span class="text-red-500">*</span>
          </h3>

          <!-- Date and Time Row -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <!-- Proposed Date -->
            <div>
              <app-date-picker
                [label]="translationService.translate('cancel.alternative.newDate')"
                [placeholder]="translationService.translate('cancel.alternative.newDate')"
                (dateChange)="onDateChange($event)"
                [required]="true"
                [minDate]="minDate()"
              ></app-date-picker>
            </div>

            <!-- Proposed Time -->
            <div>
              <app-time-picker
                [label]="translationService.translate('cancel.alternative.newTime')"
                [placeholder]="translationService.translate('cancel.alternative.selectTime')"
                (timeChange)="onTimeChange($event)"
                [required]="true"
                [startHour]="8"
                [endHour]="18"
                [intervalMinutes]="30"
              ></app-time-picker>
            </div>
          </div>

          <!-- Doctor's Note -->
          <div>
            <label for="doctor-note" class="block text-sm font-medium text-gray-700 mb-2">{{
              translationService.translate('cancel.note.label')
            }}</label>
            <textarea
              id="doctor-note"
              [value]="cancelData().doctorNote"
              (input)="onInputChange($event, 'doctorNote')"
              [placeholder]="translationService.translate('cancel.note.placeholder')"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 justify-center">
        <button
          (click)="closeModal()"
          class="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          {{ translationService.translate('cancel.button.close') }}
        </button>
        <button
          (click)="confirmCancel()"
          [disabled]="!isCancelFormValid()"
          [class]="
            isCancelFormValid()
              ? 'px-6 py-2 bg-gradient-to-r from-red-600 to-black text-white rounded-lg hover:from-red-700 hover:to-gray-900 transition-all duration-300 shadow-lg'
              : 'px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed'
          "
        >
          {{ translationService.translate('cancel.button.confirm') }}
        </button>
      </div>

      <!-- Required Fields Note -->
      <div class="mt-4 text-center">
        <p class="text-xs text-gray-500">
          <span class="text-red-500">*</span> {{ translationService.translate('cancel.required.note') }}
        </p>
      </div>
    </div>
  </div>
}
